import { useState } from 'react'
import { DynamicIcon, type IconName } from 'lucide-react/dynamic'
import { ChevronDown } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip'

const ICON_OPTIONS: IconName[] = [
  'file-plus', 'building', 'shopping-cart', 'users',
  'file-text', 'briefcase', 'store', 'message-circle',
  'layout-dashboard', 'bar-chart-3', 'calendar', 'clipboard-list',
  'code', 'database', 'globe', 'heart-pulse',
  'graduation-cap', 'image', 'mail', 'map-pin',
  'megaphone', 'music', 'newspaper', 'palette',
  'pizza', 'plane', 'rocket', 'shield',
  'ticket', 'trophy', 'truck', 'wrench',
  'bookmark', 'camera', 'cpu', 'zap',
]

const formatIconLabel = (name: string) =>
  name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')

interface IconPickerProps {
  value: string
  onValueChange: (icon: string) => void
}

export function IconPicker({ value, onValueChange }: IconPickerProps) {
  const [open, setOpen] = useState(false)

  const handleSelect = (icon: IconName) => {
    onValueChange(icon)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between font-normal">
          <span className="flex items-center gap-2">
            <DynamicIcon name={value as IconName} className="h-4 w-4" />
            {formatIconLabel(value)}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <TooltipProvider delayDuration={300}>
          <div className="grid grid-cols-6 gap-1">
            {ICON_OPTIONS.map((icon) => (
              <Tooltip key={icon}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors cursor-pointer ${
                      value === icon
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent'
                    }`}
                    onClick={() => handleSelect(icon)}
                  >
                    <DynamicIcon name={icon} className="h-5 w-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {formatIconLabel(icon)}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </TooltipProvider>
      </PopoverContent>
    </Popover>
  )
}
